import React from 'react';
import { renderCvHighlighter } from '../utils/cvHighlighter';

// ponytail: inline SVG icons — no new dependency
const IcoGeburt = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);
const IcoAdresse = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
  </svg>
);
const IcoTelefon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.3a2 2 0 0 1 2-2.18h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
  </svg>
);
const IcoEmail = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
  </svg>
);
const IcoLink = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
  </svg>
);
const kurzUrl = url => (url || '').replace(/^https?:\/\/(www\.)?/, '');

function gruppiereKenntnisse(kenntnisse = []) {
  const kategorisierte = kenntnisse.some(k => (k.kategorie || '').trim());
  if (!kategorisierte) return null;
  const gruppen = [];
  const indexByKategorie = new Map();
  kenntnisse.forEach((kenntnis) => {
    const titel = (kenntnis.titel || '').trim();
    if (!titel) return;
    const kategorie = (kenntnis.kategorie || '').trim() || 'Weitere Kenntnisse';
    if (!indexByKategorie.has(kategorie)) {
      indexByKategorie.set(kategorie, gruppen.length);
      gruppen.push({ kategorie, titel: [] });
    }
    gruppen[indexByKategorie.get(kategorie)].titel.push(titel);
  });
  return gruppen;
}

export function Kopf({ profil, nutzer, fotoUrl, onFotoClick, markierungen = [] }) {
  const vollName = `${nutzer?.vorname || ''} ${nutzer?.nachname || ''}`.trim() || nutzer?.name || '';
  const strasseZeile = [nutzer?.strasse, nutzer?.hausnummer].filter(Boolean).join(' ');
  const plzStadtZeile = [nutzer?.plz, nutzer?.ort].filter(Boolean).join(' ');
  // ponytail: anschrift is a single free-text string when strasse/plz/ort aren't
  // split in the profile; break it before the PLZ (4-5 digits) so street and
  // PLZ+Ort still land on separate lines. Ceiling: house numbers >=4 digits
  // confuse the split — acceptable, rare in German addresses.
  const anschriftSplit = nutzer?.anschrift?.match(/^(.*?),?\s*(\d{4,5}\s+.+)$/);
  const adresseZeilen = (strasseZeile || plzStadtZeile)
    ? [strasseZeile, plzStadtZeile].filter(Boolean)
    : anschriftSplit ? [anschriftSplit[1].trim(), anschriftSplit[2].trim()]
    : nutzer?.anschrift ? [nutzer.anschrift] : [];

  const kontaktZeilen = [
    nutzer?.geburtsdatum  ? { ico: <IcoGeburt />, label: 'Geburtstag', zeilen: [nutzer.geburtsdatum.split('-').reverse().join('.')] } : null,
    adresseZeilen.length  ? { ico: <IcoAdresse />, label: 'Adresse',   zeilen: adresseZeilen }                     : null,
    nutzer?.telefon       ? { ico: <IcoTelefon />, label: 'Telefon',   zeilen: [nutzer.telefon] }                  : null,
    nutzer?.email         ? { ico: <IcoEmail />,   label: 'E-Mail',    zeilen: [nutzer.email] }                    : null,
    nutzer?.linkedin      ? { ico: <IcoLink />,    label: 'LinkedIn',  zeilen: [kurzUrl(nutzer.linkedin)] }        : null,
    nutzer?.portfolio     ? { ico: <IcoLink />,    label: 'Website',   zeilen: [kurzUrl(nutzer.portfolio)] }       : null,
    nutzer?.github        ? { ico: <IcoLink />,    label: 'GitHub',    zeilen: [kurzUrl(nutzer.github)] }          : null,
  ].filter(Boolean);

  return (
    <div className="cv-pf-kopf" data-role="kopf">
      <div className="cv-pf-kopf-links">
        <div className="cv-pf-label">LEBENSLAUF</div>
        <h1 className="cv-pf-name">{vollName}</h1>
        <div className="cv-pf-name-linie" />
        {kontaktZeilen.length > 0 && (
          <div className="cv-pf-kontakt-grid">
            {kontaktZeilen.map(({ ico, label, zeilen }, i) => (
              <React.Fragment key={i}>
                <div className="cv-pf-kontakt-key">{ico}<span>{label}</span></div>
                <div className="cv-pf-kontakt-val">
                  {zeilen.map((z, j) => <div key={j}>{z}</div>)}
                </div>
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
      <div className="cv-pf-foto-block">
        {fotoUrl ? (
          <img
            src={fotoUrl}
            className="cv-pf-foto"
            alt=""
            onClick={onFotoClick || undefined}
            style={onFotoClick ? { cursor: 'pointer' } : undefined}
          />
        ) : (
          <button
            className="cv-pf-foto-placeholder"
            onClick={onFotoClick || undefined}
            title="Foto hinzufügen"
          >
            <span>+</span>
            <span className="cv-pf-foto-label">Foto</span>
          </button>
        )}
      </div>
    </div>
  );
}

export function Sektion({ s, sektionIdx, zeigeTitel = true, vonEintrag = 0, bisEintrag, markierungen = [], skeleton = false }) {
  const eintraege = s.typ === 'eintraege' ? s.eintraege.slice(vonEintrag, bisEintrag) : [];

  return (
    <div className="cv-pf-sektion">
      {zeigeTitel && (
        <h2
          className="cv-pf-sektion-titel"
          {...(sektionIdx !== undefined ? { 'data-sektion-titel': sektionIdx } : {})}
        >
          {s.name}
        </h2>
      )}

      {s.typ === 'eintraege' && (
        <div className="cv-pf-eintraege">
          {eintraege.map((e, localIdx) => {
            const globalIdx = vonEintrag + localIdx;
            // Firma und Ort stecken als "Firma · Ort" in einem Feld — für die Skelett-Kapseln aufteilen,
            // damit der Ort auch beim Bearbeiten einen eigenen Platzhalter bekommt.
            const org = e.arbeitgeber || e.firma || '';
            const sepIdx = org.indexOf(' · ');
            const firma = sepIdx >= 0 ? org.slice(0, sepIdx) : org;
            const ort = sepIdx >= 0 ? org.slice(sepIdx + 3) : '';
            return (
              <div
                key={globalIdx}
                className="cv-pf-eintrag"
                {...(sektionIdx !== undefined ? { 'data-eintrag': `${sektionIdx}-${globalIdx}` } : {})}
              >
                <div className="cv-pf-eintrag-datum">
                  {e.datum ? e.datum : (skeleton && <span className="cv-skel cv-skel-datum" />)}
                </div>
                <div className="cv-pf-eintrag-inhalt">
                  {skeleton ? (
                    <div className="cv-pf-eintrag-firma cv-skel-titel-row">
                      {firma ? renderCvHighlighter(firma, markierungen)
                             : <span className="cv-skel cv-skel-titel-teil" />}
                      <span className="cv-skel-sep">·</span>
                      {ort ? renderCvHighlighter(ort, markierungen)
                           : <span className="cv-skel cv-skel-titel-teil" />}
                    </div>
                  ) : (
                    org && (
                      <div className="cv-pf-eintrag-firma">
                        {renderCvHighlighter(org, markierungen)}
                      </div>
                    )
                  )}
                  {((e.jobtitel || e.rolle) || skeleton) && (
                    <div className="cv-pf-eintrag-rolle">
                      {(e.jobtitel || e.rolle)
                        ? renderCvHighlighter(e.jobtitel || e.rolle, markierungen)
                        : <span className="cv-skel cv-skel-titel-teil" />}
                    </div>
                  )}
                  {skeleton ? (
                    (e.bullets?.length > 0) ? (
                      <ul className="cv-pf-bullets">
                        {e.bullets.map((b, j) => (
                          <li key={j}>
                            {(b || '').trim()
                              ? renderCvHighlighter(b, markierungen)
                              : <span className="cv-skel cv-skel-bullet" />}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <ul className="cv-pf-bullets">
                        <li><span className="cv-skel cv-skel-bullet" /></li>
                        <li><span className="cv-skel cv-skel-bullet cv-skel-bullet-short" /></li>
                      </ul>
                    )
                  ) : (
                    (e.bullets || []).some(b => (b || '').trim()) && (
                      <ul className="cv-pf-bullets">
                        {e.bullets.filter(b => (b || '').trim()).map((b, j) => (
                          <li key={j}>{renderCvHighlighter(b, markierungen)}</li>
                        ))}
                      </ul>
                    )
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {s.typ === 'kenntnisse' && (() => {
        const gruppen = gruppiereKenntnisse(s.kenntnisse || []);
        if (gruppen) {
          return (
            <div className="cv-pf-skills-grouped">
              {gruppen.map((gruppe) => (
                <div className="cv-pf-skill-row" key={gruppe.kategorie}>
                  <div className="cv-pf-skill-category">{gruppe.kategorie}</div>
                  <div className="cv-pf-skill-list">{gruppe.titel.join(' · ')}</div>
                </div>
              ))}
            </div>
          );
        }
        return (
          <p className="cv-pf-skills">
            {(s.kenntnisse || []).map(k => k.titel).filter(Boolean).join(' · ')}
          </p>
        );
      })()}

      {s.typ === 'bullets' && (
        <ul className="cv-pf-bullets cv-pf-bullets-sektion">
          {(s.bullets || []).map((b, i) => (
            <li key={i}>{renderCvHighlighter(b, markierungen)}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function Footer({ nutzer }) {
  const vollName = `${nutzer?.vorname || ''} ${nutzer?.nachname || ''}`.trim() || nutzer?.name || '';
  const adresse = nutzer?.anschrift || [nutzer?.strasse, nutzer?.hausnummer, nutzer?.plz, nutzer?.ort].filter(Boolean).join(' ');
  const kontakt = [vollName, adresse, nutzer?.telefon, nutzer?.email].filter(Boolean).join(' • ');
  return <div className="cv-pf-footer">{kontakt}</div>;
}
